import { useState, useCallback } from 'react'
import { useSSE, type SSEEvent, type ProgressSSEMessage } from './useSSE'
import { downloadFileFromUrl } from '@/utils/downloadFile'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import type { ExportRead } from '@/types'

export type ExportStatus = 'idle' | 'processing' | 'completed' | 'error'

export interface ExportState {
  status: ExportStatus
  exportId: string | null
  error: string | null
  eventsUrl: string | null
  progress: number
}

const initialState: ExportState = {
  status: 'idle',
  exportId: null,
  error: null,
  eventsUrl: null,
  progress: 0,
}

export interface CreateExportResponse {
  id: string
  processing_id: string
  status: 'PENDING'
}

export interface UseExportOptions<TRequest> {
  createExportFn: (request?: TRequest) => Promise<CreateExportResponse>
  getExportFn: (exportId: string) => Promise<ExportRead>
  filenamePrefix: string
  fileExtension?: string
}

/**
 * Generic hook to orchestrate an export flow:
 * 1. POST to create export job
 * 2. Subscribe to SSE for progress
 * 3. GET export details on completion
 * 4. Download the file from download_url
 */
export function useExport<TRequest = void>(
  options: UseExportOptions<TRequest>
) {
  const {
    createExportFn,
    getExportFn,
    filenamePrefix,
    fileExtension = 'xlsx',
  } = options
  const [state, setState] = useState<ExportState>(initialState)

  // Handle SSE completion
  const handleSSEComplete = useCallback(
    async (event: SSEEvent) => {
      const { type } = event
      const data = event.data as ProgressSSEMessage

      if (type === 'complete' || data.status === 'COMPLETED') {
        // Fetch export details to get download URL
        if (state.exportId) {
          try {
            const exportData = await getExportFn(state.exportId)

            if (exportData.download_url) {
              // Extract real extension from the download URL, fall back to configured one
              const urlPath = new URL(exportData.download_url).pathname
              const urlExt = urlPath.split('.').pop()
              const ext = urlExt && urlExt.length <= 5 ? urlExt : fileExtension

              const date = new Date().toISOString().split('T')[0]
              const filename = `${filenamePrefix}-${date}.${ext}`

              await downloadFileFromUrl(exportData.download_url, filename)

              setState(prev => ({
                ...prev,
                status: 'completed',
                eventsUrl: null,
              }))

              toast.success('Export downloaded successfully')
            } else {
              throw new Error('No download URL available')
            }
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : 'Download failed'
            setState(prev => ({
              ...prev,
              status: 'error',
              error: errorMessage,
              eventsUrl: null,
            }))
            toast.error(errorMessage)
          }
        }
      } else if (type === 'error' || data.status === 'FAILED') {
        const errorMessage =
          typeof data.error === 'string'
            ? data.error
            : typeof data.message === 'string'
              ? data.message
              : 'Export failed'
        setState(prev => ({
          ...prev,
          status: 'error',
          error: errorMessage,
          eventsUrl: null,
        }))
        toast.error(errorMessage)
      }
    },
    [state.exportId, getExportFn, filenamePrefix, fileExtension]
  )

  // Handle SSE progress messages
  const handleSSEMessage = useCallback((event: SSEEvent) => {
    const data = event.data as ProgressSSEMessage
    const percent = data.percent ?? data.progress ?? undefined
    if (percent !== undefined) {
      setState(prev => ({ ...prev, progress: Math.min(percent, 100) }))
    }
  }, [])

  // Handle SSE errors
  const handleSSEError = useCallback((error: Error) => {
    setState(prev => ({
      ...prev,
      status: 'error',
      error: error.message,
      eventsUrl: null,
    }))
    toast.error('Export connection failed')
  }, [])

  // Subscribe to SSE when we have an events URL
  useSSE({
    url: state.eventsUrl,
    onMessage: handleSSEMessage,
    onComplete: handleSSEComplete,
    onError: handleSSEError,
    enabled: !!state.eventsUrl,
  })

  /**
   * Start the export process
   */
  const startExport = useCallback(
    async (request?: TRequest) => {
      setState({
        ...initialState,
        status: 'processing',
      })

      try {
        const result = await createExportFn(request)

        const eventsUrl = `/admin/processing/${result.processing_id}/events`

        setState(prev => ({
          ...prev,
          exportId: result.id,
          eventsUrl,
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to start export')
        setState(prev => ({
          ...prev,
          status: 'error',
          error: errorMessage,
        }))
        toast.error(errorMessage)
      }
    },
    [createExportFn]
  )

  /**
   * Reset the export state
   */
  const reset = useCallback(() => {
    setState(initialState)
  }, [])

  return {
    ...state,
    startExport,
    reset,
    isProcessing: state.status === 'processing',
    isCompleted: state.status === 'completed',
    isError: state.status === 'error',
    progress: state.progress,
  }
}
